/**
 * Query Deduplication Manager
 *
 * Prevents multiple simultaneous identical requests to the database by:
 * - Tracking in-flight requests
 * - Returning existing Promises for duplicate queries
 * - Automatically cleaning up completed requests
 */

class QueryDeduplicationManager {
  constructor() {
    // Map of query keys to in-flight Promise instances
    this.inFlightRequests = new Map();

    // Statistics tracking
    this.stats = {
      deduplications: 0,
      uniqueRequests: 0
    };
  }

  /**
   * Get existing Promise or create new one for a query
   * @param {string} key - Unique query key
   * @param {Function} queryFn - Async function that performs the query
   * @returns {Promise} - Either existing or new Promise
   */
  async getOrCreate(key, queryFn) {
    // Check if identical request is already in flight
    if (this.inFlightRequests.has(key)) {
      console.log(`[QUERY DEDUPE] Deduplicating request: ${key}`);
      this.stats.deduplications++;
      return this.inFlightRequests.get(key);
    }

    // Create new request
    this.stats.uniqueRequests++;

    const promise = queryFn()
      .finally(() => {
        // Clean up after request completes (success or failure)
        this.inFlightRequests.delete(key);
      });

    // Store the promise
    this.inFlightRequests.set(key, promise);

    return promise;
  }

  /**
   * Manually clear a specific in-flight request (rarely needed)
   * @param {string} key - Query key to clear
   * @returns {boolean} - True if key was removed
   */
  clear(key) {
    return this.inFlightRequests.delete(key);
  }

  /**
   * Clear all in-flight requests (useful for testing/cleanup)
   */
  clearAll() {
    this.inFlightRequests.clear();
  }

  /**
   * Get current in-flight request count
   * @returns {number}
   */
  getInFlightCount() {
    return this.inFlightRequests.size;
  }

  /**
   * Get all in-flight request keys (for debugging)
   * @returns {Array<string>}
   */
  getInFlightKeys() {
    return Array.from(this.inFlightRequests.keys());
  }

  /**
   * Get deduplication statistics
   * @returns {Object} - Stats with deduplications, unique requests, savings
   */
  getStats() {
    const total = this.stats.deduplications + this.stats.uniqueRequests;
    const savingsRate = total > 0
      ? (this.stats.deduplications / total * 100).toFixed(2)
      : 0;

    return {
      deduplications: this.stats.deduplications,
      uniqueRequests: this.stats.uniqueRequests,
      totalRequests: total,
      savingsRate: `${savingsRate}%`,
      savingsRateNumeric: parseFloat(savingsRate),
      currentInFlight: this.inFlightRequests.size
    };
  }

  /**
   * Reset statistics (useful for testing)
   */
  resetStats() {
    this.stats = {
      deduplications: 0,
      uniqueRequests: 0
    };
  }
}

// Export singleton instance
export const queryDeduplicationManager = new QueryDeduplicationManager();

// Export class for testing
export { QueryDeduplicationManager };
